import { useState, useMemo, useEffect } from 'react';
import { useFinance } from '@/contexts/FinanceContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Check,
  MoreHorizontal,
  Pencil,
  Trash2,
  Search,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Clock,
  CheckCircle2,
  Wallet,
  Copy,
  Tag,
  Building2,
  Contact,
  ArrowLeftRight,
  Repeat,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings2,
  FileDown,
  FileSpreadsheet,
} from 'lucide-react';
import { MonthYearNavigator } from './MonthYearNavigator';
import { TransactionModal } from './TransactionModal';
import { RecurrenceActionDialog } from './RecurrenceActionDialog';
import { BulkCostCenterDialog } from './BulkCostCenterDialog';
import { BulkCounterpartyDialog } from './BulkCounterpartyDialog';
import { MarkAsPaidDialog } from './MarkAsPaidDialog';
import { exportTransactionsCsv, exportTransactionsPdf } from './exportTransactions';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { usePersistedClientState } from '@/hooks/usePersistedClientState';
import type { FinanceTransaction, RecurrenceActionScope } from '@/types/finance';

const fmt = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

type SortField =
  | 'dueDate'
  | 'description'
  | 'category'
  | 'costCenter'
  | 'amount'
  | 'status'
  | 'account';
type ColumnVisibility = { costCenter: boolean; status: boolean; account: boolean };

export function TransactionList() {
  const {
    getFilteredTransactions,
    getCategoryById,
    getAccountById,
    getCostCenterById,
    categories,
    costCenters,
    counterparties,
    deleteTransaction,
    deleteRecurringTransaction,
    bulkMarkCompleted,
    bulkMarkPending,
    bulkUpdateCostCenter,
    bulkUpdateCounterparty,
    bulkDelete,
    filters,
    setFilters,
    refetchAllData,
    isRefetching,
    isLoading,
  } = useFinance();

  const [searchQuery, setSearchQuery] = usePersistedClientState<string>('searchQuery', '');
  const [editTransaction, setEditTransaction] = useState<FinanceTransaction | undefined>();
  const [duplicateTransaction, setDuplicateTransaction] = useState<
    FinanceTransaction | undefined
  >();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [recurrenceAction, setRecurrenceAction] = useState<FinanceTransaction | null>(null);
  const [recurrenceActionType, setRecurrenceActionType] = useState<'edit' | 'delete'>('edit');
  const [pendingEditScope, setPendingEditScope] = useState<RecurrenceActionScope | null>(null);

  const [typeFilter, setTypeFilter] = usePersistedClientState<
    'all' | 'income' | 'expense' | 'transfer'
  >('typeFilter', 'all');
  const [selectedCategory, setSelectedCategory] = usePersistedClientState<string>(
    'selectedCategory',
    'all',
  );
  const [selectedCostCenter, setSelectedCostCenter] = usePersistedClientState<string>(
    'selectedCostCenter',
    'all',
  );
  // Filtro de status: 'all' | 'overdue' (atrasadas) | 'pending' (a vencer) | 'completed' (pagas)
  const [statusFilter, setStatusFilter] = usePersistedClientState<
    'all' | 'overdue' | 'pending' | 'completed'
  >('statusFilter', 'all');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCcOpen, setBulkCcOpen] = useState(false);
  const [bulkCounterpartyOpen, setBulkCounterpartyOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [markAsPaidTx, setMarkAsPaidTx] = useState<FinanceTransaction | null>(null);

  const [currentPage, setCurrentPage] = usePersistedClientState<number>('currentPage', 1);
  const [itemsPerPage, setItemsPerPage] = usePersistedClientState<number>('itemsPerPage', 50);
  const [sortField, setSortField] = usePersistedClientState<SortField>('sortField', 'dueDate');
  const [sortDirection, setSortDirection] = usePersistedClientState<'asc' | 'desc'>(
    'sortDirection',
    'desc',
  );

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
    try {
      const saved = localStorage.getItem('txColumnVisibility');
      return saved ? JSON.parse(saved) : { costCenter: true, status: true, account: true };
    } catch {
      return { costCenter: true, status: true, account: true };
    }
  });

  const toggleColumn = (col: keyof ColumnVisibility) => {
    const next = { ...columnVisibility, [col]: !columnVisibility[col] };
    setColumnVisibility(next);
    localStorage.setItem('txColumnVisibility', JSON.stringify(next));
  };

  const selectedYear = filters.year;
  const selectedMonth = filters.month ?? new Date().getMonth();
  const showFullYear = filters.month === undefined;

  // Limpa seleção sempre que filtros mudam — evita ações em itens não-visíveis
  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    filters.year,
    filters.month,
    filters.accountId,
    filters.costCenterId,
    typeFilter,
    selectedCategory,
    selectedCostCenter,
    statusFilter,
    searchQuery,
  ]);

  const handleNavigate = (m: number, y: number) => setFilters({ ...filters, month: m, year: y });
  const handleShowFullYear = (full: boolean, y?: number) => {
    if (full) {
      const { month: _m, ...rest } = filters;
      setFilters({ ...rest, year: y ?? filters.year });
    } else {
      setFilters({
        ...filters,
        year: y ?? filters.year,
        month: filters.month ?? new Date().getMonth(),
      });
    }
  };

  const getStatusPriority = (t: FinanceTransaction) => {
    if (t.status === 'completed') return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(t.dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today ? 2 : 1;
  };

  const transactions = useMemo(() => {
    let filtered = getFilteredTransactions();
    if (typeFilter !== 'all') filtered = filtered.filter((t) => t.type === typeFilter);
    filtered = filtered.filter((t) => t.dueDate.getFullYear() === selectedYear);
    if (!showFullYear) filtered = filtered.filter((t) => t.dueDate.getMonth() === selectedMonth);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((t) => t.description.toLowerCase().includes(q));
    }
    if (selectedCategory !== 'all')
      filtered = filtered.filter((t) => t.categoryId === selectedCategory);
    if (selectedCostCenter !== 'all')
      filtered = filtered.filter(
        (t) =>
          t.costCenterId === selectedCostCenter ||
          (t.costCenterSplits?.some((s) => s.costCenterId === selectedCostCenter) ?? false),
      );
    if (statusFilter !== 'all') {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      filtered = filtered.filter((t) => {
        if (statusFilter === 'completed') return t.status === 'completed';
        if (statusFilter === 'overdue') return t.status !== 'completed' && t.dueDate < startOfToday;
        if (statusFilter === 'pending')
          return t.status !== 'completed' && t.dueDate >= startOfToday;
        return true;
      });
    }

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'dueDate':
          cmp = a.dueDate.getTime() - b.dueDate.getTime();
          break;
        case 'description':
          cmp = a.description.localeCompare(b.description, 'pt-BR');
          break;
        case 'category':
          cmp = (getCategoryById(a.categoryId)?.name ?? '').localeCompare(
            getCategoryById(b.categoryId)?.name ?? '',
            'pt-BR',
          );
          break;
        case 'costCenter': {
          // Com split, ordena pelo rótulo "Múltiplos" (agrupa transações multi-CC).
          const ccName = (t: FinanceTransaction) =>
            t.costCenterSplits && t.costCenterSplits.length > 0
              ? 'Múltiplos'
              : t.costCenterId
                ? (getCostCenterById(t.costCenterId)?.name ?? '')
                : '';
          cmp = ccName(a).localeCompare(ccName(b), 'pt-BR');
          break;
        }
        case 'amount':
          cmp = a.amount - b.amount;
          break;
        case 'status':
          cmp = getStatusPriority(a) - getStatusPriority(b);
          break;
        case 'account':
          cmp = (getAccountById(a.accountId)?.name ?? '').localeCompare(
            getAccountById(b.accountId)?.name ?? '',
            'pt-BR',
          );
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [
    getFilteredTransactions,
    filters,
    typeFilter,
    selectedMonth,
    selectedYear,
    showFullYear,
    searchQuery,
    selectedCategory,
    selectedCostCenter,
    statusFilter,
    sortField,
    sortDirection,
    getCategoryById,
    getCostCenterById,
    getAccountById,
  ]);

  useMemo(() => {
    setCurrentPage(1);
  }, [
    typeFilter,
    selectedCategory,
    selectedCostCenter,
    statusFilter,
    searchQuery,
    selectedYear,
    selectedMonth,
    showFullYear,
  ]);

  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const paginated = useMemo(
    () => transactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [transactions, currentPage, itemsPerPage],
  );

  const totals = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      overdue: transactions
        .filter((t) => t.status !== 'completed' && t.dueDate < today)
        .reduce((s, t) => s + t.amount, 0),
      completedIncome: transactions
        .filter((t) => t.status === 'completed' && t.type === 'income')
        .reduce((s, t) => s + t.amount, 0),
      completedExpense: transactions
        .filter((t) => t.status === 'completed' && t.type === 'expense')
        .reduce((s, t) => s + t.amount, 0),
      balance:
        transactions.filter((t) => t.type === 'income').reduce((s, t) => s + t.amount, 0) -
        transactions.filter((t) => t.type === 'expense').reduce((s, t) => s + t.amount, 0),
    };
  }, [transactions]);

  const getStatusBadge = (t: FinanceTransaction) => {
    let main: React.ReactNode;
    if (t.status === 'completed') {
      main = (
        <Badge className="border-income/20 bg-income/10 text-income hover:bg-income/20">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Pago
        </Badge>
      );
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(t.dueDate);
      due.setHours(0, 0, 0, 0);
      if (due < today) {
        main = (
          <Badge className="border-expense/20 bg-expense/10 text-expense hover:bg-expense/20">
            <AlertCircle className="mr-1 h-3 w-3" />
            Atrasado
          </Badge>
        );
      } else {
        main = (
          <Badge className="border-warning/20 bg-warning/10 text-warning hover:bg-warning/20">
            <Clock className="mr-1 h-3 w-3" />À Vencer
          </Badge>
        );
      }
    }

    let approval: React.ReactNode = null;
    if (t.approvalStatus === 'awaiting') {
      approval = (
        <Badge
          className="ml-1 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
          title="Aguardando aprovação do cliente"
        >
          ⏳ Aprovação
        </Badge>
      );
    } else if (t.approvalStatus === 'approved') {
      approval = (
        <Badge
          className="ml-1 border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
          title="Aprovado pelo cliente"
        >
          ✓ Aprovado
        </Badge>
      );
    } else if (t.approvalStatus === 'rejected') {
      approval = (
        <Badge
          className="ml-1 border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
          title={t.rejectionReason ?? 'Rejeitado pelo cliente'}
        >
          ✗ Rejeitado
        </Badge>
      );
    }

    return (
      <div className="inline-flex items-center">
        {main}
        {approval}
      </div>
    );
  };

  const handleEditClick = (t: FinanceTransaction) => {
    if (t.recurrenceGroupId) {
      setRecurrenceAction(t);
      setRecurrenceActionType('edit');
    } else setEditTransaction(t);
  };

  const handleDeleteClick = (t: FinanceTransaction) => {
    if (t.recurrenceGroupId) {
      setRecurrenceAction(t);
      setRecurrenceActionType('delete');
    } else setDeleteId(t.id);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-muted/50 ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  const handleRecurrenceConfirm = (scope: RecurrenceActionScope) => {
    if (!recurrenceAction) return;
    if (recurrenceActionType === 'delete') {
      deleteRecurringTransaction(recurrenceAction.id, scope, recurrenceAction);
      setRecurrenceAction(null);
    } else {
      setPendingEditScope(scope);
      setEditTransaction(recurrenceAction);
      setRecurrenceAction(null);
    }
  };

  const colCount =
    6 +
    (columnVisibility.costCenter ? 1 : 0) +
    (columnVisibility.status ? 1 : 0) +
    (columnVisibility.account ? 1 : 0);

  // Lógica de seleção
  const allFilteredIds = useMemo(() => transactions.map((t) => t.id), [transactions]);
  const allSelected =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && allFilteredIds.some((id) => selectedIds.has(id));

  // Soma do valor das transações selecionadas. Mostrado no AlertDialog de bulk
  // delete pra Anny confirmar a magnitude do que vai apagar (a ação é
  // irreversível na prática mesmo sendo soft-delete).
  const selectedTotal = useMemo(
    () =>
      transactions
        .filter((t) => selectedIds.has(t.id))
        .reduce((sum, t) => sum + Number(t.amount), 0),
    [transactions, selectedIds],
  );

  const toggleAllSelection = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allFilteredIds));
    }
  };

  const toggleRowSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAction = async (action: 'completed' | 'pending' | 'delete') => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      if (action === 'completed') await bulkMarkCompleted(ids);
      else if (action === 'pending') await bulkMarkPending(ids);
      else if (action === 'delete') await bulkDelete(ids);
      setSelectedIds(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkCostCenter = async (costCenterId: string | null) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await bulkUpdateCostCenter(ids, costCenterId);
    setSelectedIds(new Set());
  };

  const handleBulkCounterparty = async (counterpartyId: string | null) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await bulkUpdateCounterparty(ids, counterpartyId);
    setSelectedIds(new Set());
  };

  const { client } = useCurrentClient();

  const exportInput = useMemo(() => {
    const rows = transactions.map((t) => ({
      ...t,
      accountName: getAccountById(t.accountId)?.name ?? '—',
      categoryName: getCategoryById(t.categoryId)?.name ?? '—',
      ...(t.costCenterSplits && t.costCenterSplits.length > 0
        ? { costCenterName: `Múltiplos (${t.costCenterSplits.length})` }
        : t.costCenterId
          ? { costCenterName: getCostCenterById(t.costCenterId)?.name ?? '—' }
          : {}),
      ...(t.counterpartyId
        ? {
            counterpartyName: counterparties.find((c) => c.id === t.counterpartyId)?.name ?? '—',
          }
        : {}),
    }));

    const filterParts: string[] = [];
    if (showFullYear) filterParts.push(`Ano ${selectedYear}`);
    else
      filterParts.push(
        `${new Date(selectedYear, selectedMonth, 1).toLocaleDateString('pt-BR', {
          month: 'long',
          year: 'numeric',
        })}`,
      );
    if (typeFilter !== 'all') {
      filterParts.push(
        `Tipo: ${typeFilter === 'income' ? 'Receitas' : typeFilter === 'expense' ? 'Despesas' : 'Transferências'}`,
      );
    }
    if (statusFilter !== 'all') {
      const labels = {
        overdue: 'Atrasados',
        pending: 'A vencer',
        completed: 'Pagos/Recebidos',
      } as const;
      filterParts.push(`Status: ${labels[statusFilter]}`);
    }
    if (searchQuery) filterParts.push(`Busca: "${searchQuery}"`);

    let title = 'Lançamentos';
    if (typeFilter === 'expense') title = 'Despesas (Contas a Pagar/Pagas)';
    else if (typeFilter === 'income') title = 'Receitas (Contas a Receber/Recebidas)';

    return {
      title,
      clientName: client?.name ?? 'Cliente',
      rows,
      filtersSummary: `Filtros: ${filterParts.join(' · ')}`,
    };
  }, [
    transactions,
    getAccountById,
    getCategoryById,
    getCostCenterById,
    counterparties,
    typeFilter,
    statusFilter,
    searchQuery,
    selectedYear,
    selectedMonth,
    showFullYear,
    client?.name,
  ]);

  const handleExportPdf = () => exportTransactionsPdf(exportInput);
  const handleExportCsv = () => exportTransactionsCsv(exportInput);

  return (
    <>
      {/* Totalizadores — clicáveis: ativam o filtro de status correspondente */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'overdue' ? 'all' : 'overdue')}
          className={`text-left transition-shadow hover:shadow-sm ${
            statusFilter === 'overdue' ? 'ring-2 ring-expense' : ''
          }`}
          aria-pressed={statusFilter === 'overdue'}
        >
          <Card className="border-expense/20 bg-expense/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-expense/10 p-2">
                  <AlertCircle className="h-5 w-5 text-expense" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Vencidos {statusFilter === 'overdue' && '(filtro ativo)'}
                  </p>
                  <p className="text-xl font-bold text-expense">{fmt(totals.overdue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}
          className={`text-left transition-shadow hover:shadow-sm ${
            statusFilter === 'completed' ? 'ring-2 ring-income' : ''
          }`}
          aria-pressed={statusFilter === 'completed'}
        >
          <Card className="border-income/20 bg-income/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-income/10 p-2">
                  <CheckCircle2 className="h-5 w-5 text-income" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Pagos/Recebidos {statusFilter === 'completed' && '(filtro ativo)'}
                  </p>
                  <div className="flex gap-3 text-sm">
                    <span className="font-semibold text-income">
                      +{fmt(totals.completedIncome)}
                    </span>
                    <span className="font-semibold text-expense">
                      -{fmt(totals.completedExpense)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-2">
                <Wallet className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Saldo do Período</p>
                <p
                  className={`text-xl font-bold ${totals.balance >= 0 ? 'text-income' : 'text-expense'}`}
                >
                  {fmt(totals.balance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader className="space-y-4 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg font-semibold">Transações</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportCsv()}
                disabled={transactions.length === 0}
                title="Exportar lançamentos visíveis em CSV"
              >
                <FileSpreadsheet size={14} className="mr-1.5" />
                Exportar CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportPdf()}
                disabled={transactions.length === 0}
                title="Exportar lançamentos visíveis em PDF"
              >
                <FileDown size={14} className="mr-1.5" />
                Exportar PDF
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <MonthYearNavigator
              month={selectedMonth}
              year={selectedYear}
              showFullYear={showFullYear}
              onNavigate={handleNavigate}
              onShowFullYearChange={handleShowFullYear}
            />
            <div className="flex flex-wrap gap-2">
              {[
                {
                  value: 'income' as const,
                  Icon: TrendingUp,
                  label: 'Receitas',
                  variant: 'income' as const,
                },
                {
                  value: 'expense' as const,
                  Icon: TrendingDown,
                  label: 'Despesas',
                  variant: 'expense' as const,
                },
                {
                  value: 'transfer' as const,
                  Icon: ArrowLeftRight,
                  label: 'Transferências',
                  variant: 'default' as const,
                },
              ].map(({ value, Icon, label, variant }) => (
                <Button
                  key={value}
                  variant={typeFilter === value ? variant : 'outline'}
                  size="sm"
                  onClick={() => setTypeFilter(typeFilter === value ? 'all' : value)}
                  className="gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Button>
              ))}
            </div>
            <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar transações..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchAllData()}
                disabled={isRefetching}
                title="Atualizar"
              >
                <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-9 w-[160px]">
                <Tag className="mr-2 h-4 w-4 shrink-0" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas categorias</SelectItem>
                {categories
                  .filter((c) => typeFilter === 'all' || c.type === typeFilter)
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
              <SelectTrigger className="h-9 w-[180px]">
                <Building2 className="mr-2 h-4 w-4 shrink-0" />
                <SelectValue placeholder="Centro de Custo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos centros</SelectItem>
                {costCenters
                  .filter((cc) => cc.isActive)
                  .map((cc) => (
                    <SelectItem key={cc.id} value={cc.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: cc.color }}
                        />
                        {cc.name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
            >
              <SelectTrigger className="h-9 w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos status</SelectItem>
                <SelectItem value="overdue">Atrasados</SelectItem>
                <SelectItem value="pending">A vencer</SelectItem>
                <SelectItem value="completed">Pagos/Recebidos</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <Settings2 className="mr-2 h-4 w-4" />
                  Colunas
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48" align="end">
                <div className="space-y-2">
                  <h4 className="mb-3 text-sm font-medium">Colunas visíveis</h4>
                  {(
                    [
                      ['costCenter', 'Centro de Custo'],
                      ['status', 'Status'],
                      ['account', 'Conta'],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`col-${key}`}
                        checked={columnVisibility[key]}
                        onCheckedChange={() => toggleColumn(key)}
                      />
                      <Label htmlFor={`col-${key}`} className="cursor-pointer text-sm font-normal">
                        {label}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>

        <CardContent>
          {selectedIds.size > 0 && (
            <div className="sticky top-0 z-20 mb-3 bg-card pt-2">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 shadow-sm">
                <span className="text-sm font-medium text-primary">
                  {selectedIds.size} selecionada{selectedIds.size === 1 ? '' : 's'}
                </span>
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="income"
                    disabled={bulkBusy}
                    onClick={() => handleBulkAction('completed')}
                  >
                    <Check size={14} className="mr-1.5" />
                    Marcar Pago
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkBusy}
                    onClick={() => handleBulkAction('pending')}
                  >
                    <Clock size={14} className="mr-1.5" />
                    Marcar Pendente
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkBusy}
                    onClick={() => setBulkCcOpen(true)}
                  >
                    <Building2 size={14} className="mr-1.5" />
                    C. Custo
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkBusy}
                    onClick={() => setBulkCounterpartyOpen(true)}
                  >
                    <Contact size={14} className="mr-1.5" />
                    Contraparte
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={bulkBusy}
                    onClick={() => setBulkDeleteOpen(true)}
                  >
                    <Trash2 size={14} className="mr-1.5" />
                    Excluir
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={bulkBusy}
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Limpar
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleAllSelection}
                      aria-label="Selecionar todas as transações filtradas"
                    />
                  </TableHead>
                  <SortableHeader field="dueDate" className="w-[100px]">
                    Data
                  </SortableHeader>
                  <SortableHeader field="description">Descrição</SortableHeader>
                  <SortableHeader field="category">Categoria</SortableHeader>
                  {columnVisibility.costCenter && (
                    <SortableHeader field="costCenter">C. Custo</SortableHeader>
                  )}
                  <SortableHeader field="amount" className="text-right">
                    Valor
                  </SortableHeader>
                  {columnVisibility.status && (
                    <SortableHeader field="status">Status</SortableHeader>
                  )}
                  {columnVisibility.account && (
                    <SortableHeader field="account">Conta</SortableHeader>
                  )}
                  <TableHead className="w-[100px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading || isRefetching ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell>
                        <Skeleton className="h-4 w-4 rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-4 w-4 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      {columnVisibility.costCenter && (
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <Skeleton className="ml-auto h-4 w-20" />
                      </TableCell>
                      {columnVisibility.status && (
                        <TableCell>
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </TableCell>
                      )}
                      {columnVisibility.account && (
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <Skeleton className="ml-auto h-8 w-8 rounded" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                        <Search className="h-8 w-8 opacity-50" />
                        <p className="font-medium">
                          Nenhuma transação encontrada
                          {showFullYear
                            ? ` em ${selectedYear}`
                            : ` em ${new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
                                'pt-BR',
                                { month: 'long', year: 'numeric' },
                              )}`}
                        </p>
                        <p className="max-w-md text-xs">
                          Filtro ativo:{' '}
                          <strong>
                            {showFullYear
                              ? `ano ${selectedYear} inteiro`
                              : `${new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
                                  'pt-BR',
                                  { month: 'long' },
                                )} / ${selectedYear}`}
                          </strong>
                          {typeFilter !== 'all' && (
                            <>
                              {' · '}
                              <strong>
                                {typeFilter === 'income'
                                  ? 'Receitas'
                                  : typeFilter === 'expense'
                                    ? 'Despesas'
                                    : 'Transferências'}
                              </strong>
                            </>
                          )}
                          {selectedCategory !== 'all' && (
                            <>
                              {' · '}
                              <strong>categoria específica</strong>
                            </>
                          )}
                          {selectedCostCenter !== 'all' && (
                            <>
                              {' · '}
                              <strong>centro de custo específico</strong>
                            </>
                          )}
                          {statusFilter !== 'all' && (
                            <>
                              {' · '}
                              <strong>
                                {statusFilter === 'overdue'
                                  ? 'Atrasados'
                                  : statusFilter === 'pending'
                                    ? 'A vencer'
                                    : 'Pagos/Recebidos'}
                              </strong>
                            </>
                          )}
                          {filters.accountId !== 'all' && (
                            <>
                              {' · '}
                              <strong>conta específica</strong>
                            </>
                          )}
                          {filters.viewMode !== 'all' && (
                            <>
                              {' · '}
                              <strong>
                                {filters.viewMode === 'realized' ? 'só realizadas' : 'só previstas'}
                              </strong>
                            </>
                          )}
                          {searchQuery && (
                            <>
                              {' · '}
                              busca: <strong>"{searchQuery}"</strong>
                            </>
                          )}
                          . Transações fora desses filtros não aparecem aqui.
                        </p>
                        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-accent text-accent-foreground hover:bg-accent/90"
                            onClick={() => {
                              // Reseta TODOS os filtros (locais + globais) e mantém só o
                              // clientId/usuário. Útil quando algum filtro escondido
                              // (viewMode, accountId) está mascarando os dados sem que
                              // a Anny perceba.
                              setTypeFilter('all');
                              setSelectedCategory('all');
                              setSelectedCostCenter('all');
                              setStatusFilter('all');
                              setSearchQuery('');
                              const { month: _m, ...rest } = filters;
                              void _m;
                              setFilters({
                                ...rest,
                                accountId: 'all',
                                viewMode: 'all',
                                costCenterId: 'all',
                              });
                            }}
                          >
                            Limpar todos os filtros
                          </Button>
                          {!showFullYear && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
                              onClick={() => {
                                const { month: _month, ...rest } = filters;
                                void _month;
                                setFilters(rest);
                              }}
                            >
                              Ver {selectedYear} inteiro
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              // Tira o filtro de mês para mostrar o ano inteiro
                              // (senão a Anny troca de ano mas continua vendo só
                              // o mês corrente do ano novo, que está vazio).
                              const { month: _month, ...rest } = filters;
                              void _month;
                              setFilters({ ...rest, year: selectedYear - 1 });
                            }}
                          >
                            <ChevronLeft size={14} className="mr-1" />
                            Ver {selectedYear - 1}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const { month: _month, ...rest } = filters;
                              void _month;
                              setFilters({ ...rest, year: selectedYear + 1 });
                            }}
                          >
                            Ver {selectedYear + 1}
                            <ChevronRight size={14} className="ml-1" />
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((t) => {
                    const category = getCategoryById(t.categoryId);
                    const account = getAccountById(t.accountId);
                    const costCenter = t.costCenterId ? getCostCenterById(t.costCenterId) : null;
                    const counterparty = t.counterpartyId
                      ? counterparties.find((c) => c.id === t.counterpartyId)
                      : null;
                    const destAccount = t.destinationAccountId
                      ? getAccountById(t.destinationAccountId)
                      : null;

                    const isSelected = selectedIds.has(t.id);
                    return (
                      <TableRow
                        key={t.id}
                        className={`group ${isSelected ? 'bg-primary/5' : ''}`}
                        data-state={isSelected ? 'selected' : undefined}
                      >
                        <TableCell className="w-[40px]">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRowSelection(t.id)}
                            aria-label={`Selecionar transação ${t.description}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {format(t.dueDate, 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {t.type === 'income' ? (
                              <TrendingUp className="h-4 w-4 shrink-0 text-income" />
                            ) : t.type === 'transfer' ? (
                              <ArrowLeftRight className="h-4 w-4 shrink-0 text-primary" />
                            ) : (
                              <TrendingDown className="h-4 w-4 shrink-0 text-expense" />
                            )}
                            <div className="flex min-w-0 flex-col">
                              <span
                                className="max-w-[280px] whitespace-normal break-words"
                                title={t.description}
                              >
                                {t.description}
                              </span>
                              {counterparty && (
                                <span
                                  className="max-w-[200px] truncate text-xs text-muted-foreground"
                                  title={counterparty.name}
                                >
                                  {counterparty.name}
                                </span>
                              )}
                            </div>
                            {t.recurrenceGroupId && (
                              <span title="Recorrente">
                                <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" />
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {t.categorySplits && t.categorySplits.length > 0 ? (
                            <span
                              className="inline-flex cursor-help items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                              title={t.categorySplits
                                .map((s) => {
                                  const c = getCategoryById(s.categoryId);
                                  return `${c?.name ?? '—'}: ${new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(s.amount)}`;
                                })
                                .join('\n')}
                            >
                              Múltiplas ({t.categorySplits.length})
                            </span>
                          ) : category ? (
                            <div className="flex items-center gap-2">
                              <div
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: category.color }}
                              />
                              <span className="text-sm">{category.name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        {columnVisibility.costCenter && (
                          <TableCell>
                            {t.costCenterSplits && t.costCenterSplits.length > 0 ? (
                              <span
                                className="inline-flex cursor-help items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                                title={t.costCenterSplits
                                  .map((s) => {
                                    const cc = getCostCenterById(s.costCenterId);
                                    return `${cc?.name ?? '—'}: ${new Intl.NumberFormat('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    }).format(s.amount)}`;
                                  })
                                  .join('\n')}
                              >
                                Múltiplos ({t.costCenterSplits.length})
                              </span>
                            ) : costCenter ? (
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={{ backgroundColor: costCenter.color }}
                                />
                                <span className="text-sm">{costCenter.name}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell
                          className={`text-right font-semibold ${t.type === 'income' ? 'text-income' : t.type === 'transfer' ? 'text-primary' : 'text-expense'}`}
                        >
                          {t.type === 'income' ? '+' : t.type === 'transfer' ? '' : '-'}
                          {fmt(t.amount)}
                        </TableCell>
                        {columnVisibility.status && <TableCell>{getStatusBadge(t)}</TableCell>}
                        {columnVisibility.account && (
                          <TableCell>
                            {t.type === 'transfer' && destAccount ? (
                              <div className="flex items-center gap-1 text-sm">
                                <div
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: account?.color }}
                                />
                                <span className="max-w-[60px] truncate">{account?.name}</span>
                                <span className="text-muted-foreground">→</span>
                                <div
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: destAccount.color }}
                                />
                                <span className="max-w-[60px] truncate">{destAccount.name}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={{ backgroundColor: account?.color }}
                                />
                                <span className="max-w-[100px] truncate text-sm text-muted-foreground">
                                  {account?.name}
                                </span>
                              </div>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            {t.status !== 'completed' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-income hover:bg-income/10 hover:text-income"
                                onClick={() => setMarkAsPaidTx(t)}
                                title={
                                  t.type === 'income'
                                    ? 'Confirmar recebimento'
                                    : 'Confirmar pagamento'
                                }
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditClick(t)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDuplicateTransaction(t)}>
                                  <Copy className="mr-2 h-4 w-4" />
                                  Duplicar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteClick(t)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {transactions.length > 0 && (
            <div className="mt-4 flex flex-col items-center justify-between gap-4 border-t pt-4 sm:flex-row">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  Mostrando {(currentPage - 1) * itemsPerPage + 1}–
                  {Math.min(currentPage * itemsPerPage, transactions.length)} de{' '}
                  {transactions.length}
                </span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(v) => {
                    setItemsPerPage(Number(v));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['25', '50', '100', '200'].map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>por página</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-sm font-medium">
                  {currentPage} / {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {editTransaction && (
        <TransactionModal
          open={!!editTransaction}
          onOpenChange={(open) => {
            if (!open) {
              setEditTransaction(undefined);
              setPendingEditScope(null);
            }
          }}
          editTransaction={editTransaction}
          {...(pendingEditScope !== null ? { editScope: pendingEditScope } : {})}
        />
      )}

      {duplicateTransaction && (
        <TransactionModal
          open={!!duplicateTransaction}
          onOpenChange={(open) => !open && setDuplicateTransaction(undefined)}
          editTransaction={(() => {
            const { paymentDate: _p, recurrenceGroupId: _r, ...rest } = duplicateTransaction;
            return {
              ...rest,
              id: '',
              dueDate: new Date(),
              status: 'pending' as const,
              recurrence: 'monthly' as const,
              numberOfRepetitions: duplicateTransaction.numberOfRepetitions ?? 2,
            };
          })()}
        />
      )}

      <RecurrenceActionDialog
        open={!!recurrenceAction}
        onOpenChange={(open) => !open && setRecurrenceAction(null)}
        transaction={recurrenceAction}
        actionType={recurrenceActionType}
        onConfirm={handleRecurrenceConfirm}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir transação?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) {
                  deleteTransaction(deleteId);
                  setDeleteId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkCostCenterDialog
        open={bulkCcOpen}
        onOpenChange={setBulkCcOpen}
        count={selectedIds.size}
        onConfirm={handleBulkCostCenter}
      />

      <BulkCounterpartyDialog
        open={bulkCounterpartyOpen}
        onOpenChange={setBulkCounterpartyOpen}
        count={selectedIds.size}
        onConfirm={handleBulkCounterparty}
      />

      <MarkAsPaidDialog
        open={!!markAsPaidTx}
        onOpenChange={(o) => !o && setMarkAsPaidTx(null)}
        transaction={markAsPaidTx}
      />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selectedIds.size} transação(ões)?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Total selecionado:{' '}
                  <span className="font-semibold tabular-nums text-foreground">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(selectedTotal)}
                  </span>{' '}
                  em movimentação.
                </p>
                <p>
                  Esta ação não pode ser desfeita. Cada linha selecionada será excluída
                  individualmente — séries recorrentes não são consideradas.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await handleBulkAction('delete');
                setBulkDeleteOpen(false);
              }}
              disabled={bulkBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
